/**
 * IXD 가이드라인 검증 결과 페이지 (/guideline-check/result)
 *
 * opener(index.tsx)가 postMessage로 GuidelineCheckWithPreviewResponse를 전달한다.
 * 수신 전까지 로딩 화면, 수신 후 GuidelineCheckResultView 렌더.
 *
 * 다운로드 버튼은 headerAction 슬롯으로 주입 — 결과 뷰 자체는 다운로드 로직에 의존하지 않는다
 * (뷰어 번들이 같은 결과 뷰를 재사용하므로, 다운로드 import가 뷰에 섞이면 번들 순환이 된다).
 */

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Loader2 } from "lucide-react";

import type { GuidelineCheckWithPreviewResponse } from "@/apis/guideline/types";
import { Button } from "@/toggle-ui-kit/button";
import { useHtmlExport } from "@/lib/html-export";
import { GuidelineCheckResultView } from "./result-view";

// postMessage 상수 — index.tsx와 동일 리터럴
export const GUIDELINE_RESULT_READY = "guideline-check-result-ready";
export const GUIDELINE_RESULT_DATA = "guideline-check-result-data";

interface ResultDataMessage {
  type: typeof GUIDELINE_RESULT_DATA;
  data: GuidelineCheckWithPreviewResponse;
  filename: string;
}

// ---------------------------------------------------------------------------
// 로딩/메시지 화면
// ---------------------------------------------------------------------------

const LoadingScreen = ({ message }: { message: string }) => (
  <div className="flex h-screen w-full items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="size-8 animate-spin text-label-assistive" />
      <p className="body-1-normal-medium text-label-assistive">{message}</p>
    </div>
  </div>
);

const MessageScreen = ({ message }: { message: string }) => (
  <div className="flex h-screen w-full items-center justify-center bg-white">
    <p className="body-1-normal-medium text-label-assistive">{message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// 다운로드 버튼 (뷰어 번들은 동적 import로 분리)
// ---------------------------------------------------------------------------

const DownloadButton = ({
  data,
  filename,
}: {
  data: GuidelineCheckWithPreviewResponse;
  filename: string;
}) => {
  const { exportNow, isExporting } = useHtmlExport({
    build: async () => {
      const { buildGuidelineViewerHtml } = await import(
        "./export/build-viewer-html"
      );
      return {
        html: buildGuidelineViewerHtml({ data }, "IXD 가이드라인 검증 결과"),
        filename: `${filename.replace(/\.ixd$/i, "")}_guideline.html`,
      };
    },
  });

  return (
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      onClick={exportNow}
      disabled={isExporting}
    >
      {isExporting ? "준비 중..." : "HTML 다운로드"}
    </Button>
  );
};

// ---------------------------------------------------------------------------
// 결과 페이지 내부 (데이터 수신 후)
// ---------------------------------------------------------------------------

const GuidelineCheckResultPageInner = () => {
  const [received, setReceived] = useState<{
    data: GuidelineCheckWithPreviewResponse;
    filename: string;
  } | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const msg = event.data as Partial<ResultDataMessage> | undefined;
      if (msg?.type !== GUIDELINE_RESULT_DATA || !msg.data) return;
      setReceived({ data: msg.data, filename: msg.filename ?? "report.ixd" });
      window.removeEventListener("message", onMessage);
    };
    window.addEventListener("message", onMessage);

    // mount 시 opener에게 READY 전송
    if (window.opener) {
      window.opener.postMessage(
        { type: GUIDELINE_RESULT_READY },
        window.location.origin,
      );
    }

    return () => window.removeEventListener("message", onMessage);
  }, []);

  if (!received) {
    return window.opener ? (
      <LoadingScreen message="검사 결과를 연결하는 중..." />
    ) : (
      <MessageScreen message="가이드라인 검사 화면에서 다시 시도해주세요." />
    );
  }

  return (
    <GuidelineCheckResultView
      data={received.data}
      headerAction={
        <DownloadButton data={received.data} filename={received.filename} />
      }
    />
  );
};

export const GuidelineCheckResultPage = () => (
  <Sentry.ErrorBoundary
    fallback={
      <MessageScreen message="결과를 표시하는 중 오류가 발생했습니다." />
    }
  >
    <GuidelineCheckResultPageInner />
  </Sentry.ErrorBoundary>
);

export default GuidelineCheckResultPage;
